<template>
  <div class="stock-trend-list">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 20V4M5 13l7-7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 17h6M15 17h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h1>趋势指标筛选</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">基于均线、高点、连续性的综合趋势分析</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="selectedTrend"
        placeholder="请选择趋势状态"
        clearable
        class="filter-select"
      >
        <el-option label="强势上涨" value="强势上涨"></el-option>
        <el-option label="上涨" value="上涨"></el-option>
        <el-option label="震荡" value="震荡"></el-option>
        <el-option label="下跌" value="下跌"></el-option>
      </el-select>

      <el-select
        v-model="searchCode"
        placeholder="股票代码（可选）"
        filterable
        clearable
        class="search-select"
        :loading="stockListLoading"
      >
        <el-option
          v-for="item in stockCodeList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-input-number
        v-model="ma20Min"
        placeholder="MA20最低价"
        :precision="2"
        :step="0.5"
        :min="0"
        class="ma20-input"
        controls-position="right"
      >
      </el-input-number>

      <el-input-number
        v-model="ma20Max"
        placeholder="MA20最高价"
        :precision="2"
        :step="0.5"
        :min="0"
        class="ma20-input"
        controls-position="right"
      >
      </el-input-number>

      <el-date-picker
        v-model="selectedDate"
        type="date"
        placeholder="选择统计日期"
        value-format="yyyy-MM-dd"
        class="date-picker"
      >
      </el-date-picker>

      <el-button type="primary" @click="fetchData" :loading="loading">
        <i class="el-icon-search"></i>
        查询
      </el-button>
      <el-button @click="resetFilters">
        <i class="el-icon-refresh-left"></i>
        重置
      </el-button>
    </div>

    <div class="stock-count">共找到 <strong>{{ stockList.length }}</strong> 只股票</div>

    <el-table
      :data="paginatedStockList"
      v-loading="loading"
      border
      stripe
      style="width: 100%"
      class="stock-table"
    >
      <el-table-column prop="code" label="股票代码" width="120">
        <template slot-scope="scope">
          <el-button type="text" @click="viewKLine(scope.row)">
            {{ scope.row.code }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column prop="stock_name" label="股票名称" width="150">
        <template slot-scope="scope">
          {{ scope.row.stock_name || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="stat_date" label="统计日期" width="120"></el-table-column>
      <el-table-column prop="close" label="收盘价" width="100" align="right">
        <template slot-scope="scope">
          {{ scope.row.close ? parseFloat(scope.row.close).toFixed(2) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="ma5" label="5日均线" width="100" align="right">
        <template slot-scope="scope">
          {{ scope.row.ma5 ? parseFloat(scope.row.ma5).toFixed(2) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="ma20" label="20日均线" width="100" align="right">
        <template slot-scope="scope">
          {{ scope.row.ma20 ? parseFloat(scope.row.ma20).toFixed(2) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="up_20days" label="20日上涨天数" width="130" align="right">
        <template slot-scope="scope">
          <span :class="getUpDaysClass(scope.row.up_20days)">
            {{ scope.row.up_20days || '-' }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="trend_score" label="趋势得分" width="100" align="right">
        <template slot-scope="scope">
          <span :class="getScoreClass(scope.row.trend_score)">
            {{ scope.row.trend_score || '-' }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="trend" label="趋势状态" width="120">
        <template slot-scope="scope">
          <el-tag :type="getTrendTagType(scope.row.trend)" size="small">
            {{ scope.row.trend }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right" align="center">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" @click="viewTrend(scope.row)">
            <i class="el-icon-data-line"></i>
            趋势详情
          </el-button>
          <el-button size="mini" @click="viewKLine(scope.row)">
            <i class="el-icon-data-board"></i>
            K线图
          </el-button>
          <el-button
            size="mini"
            type="text"
            :loading="scope.row.adding"
            @click.stop="addToFavorite(scope.row)"
          >
            <i class="el-icon-star-on"></i>
            自选
          </el-button>
          <el-button
            size="mini"
            type="text"
            @click.stop="copyStockCode(scope.row)"
          >
            复制
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="stockList.length > 0"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, 200]"
      :page-size="pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="stockList.length"
      class="pagination"
    >
    </el-pagination>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'StockTrendList',
  data() {
    return {
      selectedTrend: '',
      searchCode: '',
      ma20Min: null,
      ma20Max: null,
      selectedDate: '',
      stockCodeList: [],
      stockListLoading: false,
      stockList: [],
      loading: false,
      currentPage: 1,
      pageSize: 50,
      userId: '1'
    }
  },
  computed: {
    paginatedStockList() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.stockList.slice(start, end)
    }
  },
  mounted() {
    this.loadStockCodeList()
    // 设置默认日期为今天
    const today = new Date().toISOString().split('T')[0]
    // this.selectedDate = today
    this.selectedDate = "2026-03-06"
    // 自动加载一次数据
    this.fetchData()
  },
  methods: {
    async loadStockCodeList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockCodeList = response.data.data
        }
      } catch (error) {
        this.$message.error('加载股票列表失败')
        console.error(error)
      } finally {
        this.stockListLoading = false
      }
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {}
        if (this.selectedTrend) {
          params.trend = this.selectedTrend
        }
        if (this.searchCode) {
          params.code = this.searchCode
        }
        if (this.ma20Min !== null && this.ma20Min !== undefined && this.ma20Min !== '') {
          params.ma20_min = this.ma20Min
        }
        if (this.ma20Max !== null && this.ma20Max !== undefined && this.ma20Max !== '') {
          params.ma20_max = this.ma20Max
        }
        if (this.selectedDate) {
          params.stat_date = this.selectedDate
        }

        const response = await axios.get('/stock/trend/integration/list', { params })

        if (response.data.status === 'success') {
          this.stockList = response.data.data
          this.currentPage = 1
          if (this.stockList.length === 0) {
            this.$message.info('未找到符合条件的股票')
          }
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        this.$message.error('查询失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.selectedTrend = ''
      this.searchCode = ''
      this.ma20Min = null
      this.ma20Max = null
      this.selectedDate = new Date().toISOString().split('T')[0]
      this.currentPage = 1
      this.fetchData()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    getUpDaysClass(days) {
      if (!days) return ''
      if (days >= 15) return 'value-strong-up'
      if (days >= 10) return 'value-positive'
      if (days >= 8) return 'value-neutral'
      return 'value-negative'
    },
    getScoreClass(score) {
      if (!score) return ''
      if (score >= 4) return 'score-high'
      if (score >= 2) return 'score-medium'
      if (score >= 1) return 'score-low'
      return 'score-very-low'
    },
    getTrendTagType(trend) {
      const typeMap = {
        '强势上涨': 'success',
        '上涨': 'success',
        '下跌': 'danger',
        '震荡': 'info'
      }
      return typeMap[trend] || 'info'
    },
    viewKLine(row) {
      this.$router.push({
        path: '/kline/day',
        query: { code: row.code }
      })
    },
    viewTrend(row) {
      this.$router.push({
        path: '/trend',
        query: { code: row.code }
      })
    },
    async addToFavorite(row) {
      this.$set(row, 'adding', true)
      try {
        const response = await axios.post('/stock/favorite/add', {
          user_id: this.userId,
          stock_code: row.code,
          stock_name: row.stock_name,
          remark: '',
          sort_order: 0
        })
        if (response.data.status === 'success') {
          this.$message.success(`已将 ${row.stock_name} 添加到自选`)
        } else {
          this.$message.error(response.data.message || '添加失败')
        }
      } catch (error) {
        console.error('添加自选失败:', error)
        this.$message.error('添加自选失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.$set(row, 'adding', false)
      }
    },
    copyStockCode(row) {
      const code = row.code
      navigator.clipboard.writeText(code).then(() => {
        this.$message.success({
          message: `已复制: ${code}`,
          duration: 2000
        })
      }).catch(err => {
        console.error('复制失败:', err)
        this.$message.error({
          message: '复制失败',
          duration: 2000
        })
      })
    }
  }
}
</script>

<style scoped>
.stock-trend-list {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.title-icon svg {
  width: 28px;
  height: 28px;
}

.page-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-decoration {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  letter-spacing: 0.5px;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.filter-select,
.search-select,
.ma20-input,
.date-picker {
  width: 220px;
}

/* 输入框和选择器样式 */
.filter-bar :deep(.el-select .el-input__inner),
.filter-bar :deep(.el-date-picker .el-input__inner),
.filter-bar :deep(.el-date-editor .el-input__inner),
.filter-bar :deep(.el-input__inner),
.filter-bar :deep(.el-input-number .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-select .el-input__inner):hover,
.filter-bar :deep(.el-date-picker .el-input__inner):hover,
.filter-bar :deep(.el-date-editor .el-input__inner):hover,
.filter-bar :deep(.el-input__inner):hover,
.filter-bar :deep(.el-input-number .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-select .el-input__inner):focus,
.filter-bar :deep(.el-date-picker .el-input__inner):focus,
.filter-bar :deep(.el-date-editor .el-input__inner):focus,
.filter-bar :deep(.el-input__inner):focus,
.filter-bar :deep(.el-input-number .el-input__inner):focus {
  border-color: #6366f1 !important;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.25) !important;
}

.filter-bar :deep(.el-date-picker .el-input__prefix),
.filter-bar :deep(.el-date-editor .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6) !important;
}

.filter-bar :deep(.el-date-picker .el-input__icon),
.filter-bar :deep(.el-date-editor .el-input__icon) {
  color: rgba(99, 102, 241, 0.6) !important;
}

.filter-bar :deep(.el-select__placeholder) {
  color: #64748b;
}

.filter-bar :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-button--primary):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}

.filter-bar :deep(.el-button--primary):active {
  transform: translateY(0);
}

.filter-bar :deep(.el-button--default) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 24px;
  color: #e2e8f0;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-button--default):hover {
  border-color: rgba(99, 102, 241, 0.4);
  background: rgba(15, 23, 42, 0.8);
}

/* 股票数量 */
.stock-count {
  margin-bottom: 16px;
  font-size: 14px;
  color: #94a3b8;
  padding: 0 4px;
}

.stock-count strong {
  color: #a5b4fc;
  font-size: 16px;
}

/* 表格样式 */
.stock-table {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  overflow: hidden;
}

.stock-table :deep(.el-table__header-wrapper) {
  background: rgba(15, 23, 42, 0.5);
}

.stock-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.8) !important;
  color: #a5b4fc;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.stock-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.stock-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.stock-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

.stock-table :deep(.el-table__body tr.current-row > td) {
  background: rgba(99, 102, 241, 0.15) !important;
}

/* 上涨天数样式 */
.value-strong-up {
  color: #22c55e;
  font-weight: 700;
}

.value-positive {
  color: #4ade80;
  font-weight: 600;
}

.value-neutral {
  color: #94a3b8;
}

.value-negative {
  color: #f87171;
  font-weight: 600;
}

/* 趋势得分样式 */
.score-high {
  color: #22c55e;
  font-weight: 700;
}

.score-medium {
  color: #4ade80;
  font-weight: 600;
}

.score-low {
  color: #fbbf24;
  font-weight: 600;
}

.score-very-low {
  color: #ef4444;
  font-weight: 600;
}

/* 操作列居中 */
.stock-table :deep(.el-table__cell.el-table__cell) {
  text-align: center;
}

.stock-table :deep(.el-table__body td.el-table__cell) {
  text-align: center;
}

/* 分页 */
.pagination {
  margin-top: 20px;
  text-align: center;
}

.pagination :deep(.el-pagination) {
  color: #94a3b8;
}

.pagination :deep(.el-pagination .el-pager li) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  margin: 0 4px;
  min-width: 32px;
  height: 32px;
  line-height: 30px;
  transition: all 0.25s ease;
}

.pagination :deep(.el-pagination .el-pager li:hover) {
  border-color: rgba(99, 102, 241, 0.4);
  color: #a5b4fc;
}

.pagination :deep(.el-pagination .el-pager li.active) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: transparent;
  color: #ffffff;
  font-weight: 600;
}

.pagination :deep(.el-pagination button) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #94a3b8;
  transition: all 0.25s ease;
}

.pagination :deep(.el-pagination button:hover) {
  border-color: rgba(99, 102, 241, 0.4);
  color: #a5b4fc;
}

.pagination :deep(.el-pagination .el-pagination__sizes .el-select .el-input .el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  border-radius: 6px;
}

.pagination :deep(.el-pagination .el-pagination__jump) {
  color: #94a3b8;
}
</style>
